import { useState } from "react";
import type { CSSProperties, ReactNode } from "react";
import { theme, radius, fontStack } from "./theme";

/**
 * ADMIN-12 — primitives UI de la console, stylées sur les tokens partagés
 * (packages/ui). Volontairement minimalistes : le squelette d'écrans (spec
 * ADMIN-12) se remplit métier en ADMIN-13/14.
 */

export function Card({ children, style }: { children: ReactNode; style?: CSSProperties }) {
  return (
    <div
      style={{
        background: theme.surface,
        border: `1px solid ${theme.line}`,
        borderRadius: radius.sheet,
        padding: 20,
        ...style,
      }}
    >
      {children}
    </div>
  );
}

export function Button({
  children,
  onClick,
  disabled,
  variant = "primary",
  type = "button",
  style,
  title,
}: {
  children: ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  variant?: "primary" | "ghost";
  type?: "button" | "submit";
  style?: CSSProperties;
  /** Infobulle — explique le geste à qui découvre l'outil (console v13.11). */
  title?: string;
}) {
  const primary = variant === "primary";
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      title={title}
      style={{
        background: primary ? theme.accent : "transparent",
        color: primary ? theme.onAccent : theme.ink,
        border: primary ? "none" : `1px solid ${theme.line}`,
        borderRadius: radius.pill,
        padding: "10px 18px",
        fontFamily: fontStack.uiSemiBold,
        fontWeight: 600,
        fontSize: 14,
        opacity: disabled ? 0.5 : 1,
        transition: "opacity .15s",
        ...style,
      }}
    >
      {children}
    </button>
  );
}

export function Field({
  label,
  ...input
}: { label: string } & React.InputHTMLAttributes<HTMLInputElement>) {
  return (
    <label style={{ display: "block", marginBottom: 14 }}>
      <span style={{ display: "block", color: theme.ink2, fontSize: 13, marginBottom: 6 }}>
        {label}
      </span>
      <input
        {...input}
        style={{
          width: "100%",
          background: theme.bg,
          color: theme.ink,
          border: `1px solid ${theme.line}`,
          borderRadius: radius.field,
          padding: "11px 14px",
          fontSize: 15,
          outline: "none",
        }}
      />
    </label>
  );
}

export function Badge({ children, tone = "neutral" }: { children: ReactNode; tone?: "neutral" | "accent" | "warning" | "error" }) {
  const bg =
    tone === "accent" ? theme.accent : tone === "warning" ? theme.warning : tone === "error" ? theme.error : theme.surface3;
  const fg = tone === "accent" ? theme.onAccent : tone === "neutral" ? theme.ink2 : "#1a1a1a";
  return (
    <span
      style={{
        display: "inline-block",
        background: bg,
        color: fg,
        borderRadius: radius.pill,
        padding: "2px 10px",
        fontSize: 12,
        fontFamily: fontStack.uiSemiBold,
        fontWeight: 600,
        whiteSpace: "nowrap",
      }}
    >
      {children}
    </span>
  );
}

/**
 * Panneau REPLIABLE — réponse au retour d'Henri (3 août) : « quand on rentre dans
 * un cas, l'interface est encore trop compliquée ».
 *
 * Le dossier montrait tout, tout le temps : le modérateur devait trier lui-même
 * ce qui le concernait à cet instant. Ici, chaque outil secondaire (preuves,
 * sanction) est REPLIÉ par défaut et annonce en une ligne ce qu'il contient. On
 * ouvre ce dont on a besoin ; le reste ne crie pas.
 */
export function Collapsible({
  title,
  hint,
  defaultOpen = false,
  accent,
  children,
}: {
  title: string;
  /** Résumé d'une ligne, visible SANS ouvrir (ex. « Compte banni le 3 août »). */
  hint?: string;
  defaultOpen?: boolean;
  /** Met le titre en évidence (sujet chaud : sanction en cours, etc.). */
  accent?: boolean;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <Card style={{ padding: 0 }}>
      <button
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        style={{
          width: "100%",
          background: "transparent",
          border: "none",
          padding: "14px 18px",
          textAlign: "left",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          gap: 10,
        }}
      >
        <span style={{ flex: 1, minWidth: 0 }}>
          <span
            style={{
              display: "block",
              fontFamily: fontStack.uiSemiBold,
              fontSize: 13,
              color: accent ? theme.accent : theme.ink2,
              textTransform: "uppercase",
              letterSpacing: 0.5,
            }}
          >
            {title}
          </span>
          {hint && !open ? (
            <span style={{ display: "block", color: theme.ink3, fontSize: 12.5, marginTop: 3 }}>
              {hint}
            </span>
          ) : null}
        </span>
        <span style={{ color: theme.ink3, fontSize: 13 }} aria-hidden>
          {open ? "▾" : "▸"}
        </span>
      </button>
      {open ? <div style={{ padding: "0 18px 18px" }}>{children}</div> : null}
    </Card>
  );
}

export function Spinner({ label }: { label?: string }) {
  return (
    <div style={{ color: theme.ink3, padding: 24, fontSize: 14 }}>{label ?? "Chargement…"}</div>
  );
}

export function ErrorBanner({ message }: { message: string }) {
  return (
    <div
      style={{
        background: "rgba(255,90,90,0.12)",
        border: `1px solid ${theme.error}`,
        color: theme.ink,
        borderRadius: radius.field,
        padding: "10px 14px",
        fontSize: 14,
        marginBottom: 14,
      }}
    >
      {message}
    </div>
  );
}
